import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Home as HomeIcon, X } from 'lucide-react';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const GW = 900, GH = 500;
const GRAV = 0.58, JUMP_V = -13.5, WALK_V = 4.5;
const PW = 28, PH = 46;
const EW = 26, EH = 42;
const LEVEL_W = 5400;
const INV_DUR = 110;
const TOTAL_FIGS = 15;
const PORTAL_X = 5260, PORTAL_Y = 360;

type GState = 'start' | 'playing' | 'saving' | 'dead' | 'win';

// Fixed launch positions for figurine fly-to-counter animation
const LAUNCH_POS = [
  {x:90, y:378},{x:195,y:338},{x:315,y:385},{x:435,y:358},
  {x:555,y:378},{x:675,y:348},{x:795,y:372},{x:145,y:292},
  {x:275,y:258},{x:415,y:295},{x:545,y:265},{x:695,y:285},
  {x:815,y:255},{x:235,y:415},{x:665,y:415},
];

interface Plat  { x:number; y:number; w:number; h:number; }
interface Enemy { x:number; y:number; vx:number; alive:boolean; left:number; right:number; type:'skel'|'ghost'; anim:number; }
interface Fig   { x:number; y:number; col:boolean; }

// ─── LEVEL DATA ──────────────────────────────────────────────────────────────
const PLATFORMS: Plat[] = [
  {x:0,    y:440, w:580, h:60}, {x:660,  y:440, w:520, h:60},
  {x:1270, y:440, w:420, h:60}, {x:1780, y:440, w:520, h:60},
  {x:2400, y:440, w:460, h:60}, {x:2960, y:440, w:580, h:60},
  {x:3640, y:440, w:480, h:60}, {x:4220, y:440, w:420, h:60},
  {x:4740, y:440, w:660, h:60},
  {x:200,  y:360, w:130, h:20}, {x:400,  y:300, w:110, h:20},
  {x:570,  y:348, w:120, h:20}, {x:730,  y:278, w:100, h:20},
  {x:910,  y:358, w:130, h:20}, {x:1060, y:288, w:90,  h:20},
  {x:1200, y:350, w:120, h:20}, {x:1380, y:270, w:100, h:20},
  {x:1560, y:368, w:120, h:20}, {x:1720, y:298, w:100, h:20},
  {x:1900, y:362, w:140, h:20}, {x:2090, y:282, w:90,  h:20},
  {x:2260, y:212, w:110, h:20}, {x:2460, y:342, w:130, h:20},
  {x:2640, y:272, w:100, h:20}, {x:2820, y:380, w:120, h:20},
  {x:3000, y:300, w:100, h:20}, {x:3170, y:240, w:90,  h:20},
  {x:3360, y:352, w:140, h:20}, {x:3550, y:280, w:100, h:20},
  {x:3750, y:358, w:120, h:20}, {x:3940, y:278, w:90,  h:20},
  {x:4140, y:360, w:130, h:20}, {x:4330, y:298, w:100, h:20},
  {x:4520, y:378, w:120, h:20}, {x:4720, y:298, w:100, h:20},
  {x:4920, y:342, w:120, h:20}, {x:5120, y:378, w:280, h:20},
];

const FIGS_INIT: {x:number,y:number}[] = [
  {x:265,y:330},{x:455,y:270},{x:625,y:320},{x:780,y:248},
  {x:965,y:328},{x:1105,y:258},{x:1435,y:240},{x:1775,y:268},
  {x:1975,y:332},{x:2145,y:252},{x:2315,y:182},{x:2695,y:242},
  {x:3050,y:270},{x:3220,y:210},{x:5210,y:330},
];

const ENEMIES_INIT: {x:number,y:number,l:number,r:number,t:'skel'|'ghost'}[] = [
  {x:310,  y:440, l:220,  r:370,  t:'skel'},
  {x:780,  y:440, l:680,  r:880,  t:'skel'},
  {x:1060, y:288, l:1022, r:1130, t:'ghost'},
  {x:1380, y:270, l:1340, r:1460, t:'skel'},
  {x:1920, y:440, l:1840, r:2060, t:'skel'},
  {x:2460, y:342, l:2420, r:2560, t:'ghost'},
  {x:3360, y:352, l:3320, r:3480, t:'skel'},
  {x:4330, y:298, l:4290, r:4410, t:'ghost'},
  {x:4950, y:440, l:4790, r:5060, t:'skel'},
];

// ─── DRAW: BACKGROUND ────────────────────────────────────────────────────────
function drawBg(ctx: CanvasRenderingContext2D, camX: number, frame: number) {
  const sky = ctx.createLinearGradient(0,0,0,GH);
  sky.addColorStop(0,'#06041a'); sky.addColorStop(0.55,'#160d30'); sky.addColorStop(1,'#0c0810');
  ctx.fillStyle = sky; ctx.fillRect(0,0,GW,GH);
  const mx = 720 - camX*0.018, my = 78;
  const mg = ctx.createRadialGradient(mx,my,3,mx,my,60);
  mg.addColorStop(0,'rgba(255,245,190,0.22)'); mg.addColorStop(1,'rgba(0,0,0,0)');
  ctx.fillStyle = mg; ctx.beginPath(); ctx.arc(mx,my,60,0,Math.PI*2); ctx.fill();
  ctx.fillStyle = '#ede5b8'; ctx.beginPath(); ctx.arc(mx,my,27,0,Math.PI*2); ctx.fill();
  ctx.fillStyle = '#d5cc90';
  [[mx-9,my-5,5.5],[mx+8,my+7,3.5],[mx-3,my+10,4.5]].forEach(([cx,cy,r])=>{
    ctx.beginPath(); ctx.arc(cx as number,cy as number,r as number,0,Math.PI*2); ctx.fill();
  });
  for (let i = 0; i < 68; i++) {
    const sx=(i*137+51)%GW, sy=(i*83+17)%(GH*0.52);
    const tw=0.3+0.7*Math.abs(Math.sin(frame*0.018+i*0.9));
    ctx.globalAlpha=tw; ctx.fillStyle='#fff'; ctx.fillRect(sx-1,sy-1,i%5===0?3:2,i%5===0?3:2);
  }
  ctx.globalAlpha=1;
  const mo=camX*0.06;
  const mpts=[0,190,80,115,200,155,340,85,480,130,620,72,780,108,940,62,1100,122,1300,90,1500,140,1700,78];
  ctx.fillStyle='#0c0820'; ctx.beginPath(); ctx.moveTo(0,GH);
  for (let i=0;i<mpts.length;i+=2) ctx.lineTo((mpts[i]-mo%1700+1700)%1700+(mpts[i]-mo%1700<0?1700:0)-1700,mpts[i+1]);
  ctx.lineTo(GW,GH); ctx.closePath(); ctx.fill();
  const co=camX*0.14;
  const drawTower=(bx:number,bh:number)=>{
    const tx=((bx-co%2000+2000)%2000)-200;
    if(tx>GW+80)return;
    ctx.fillStyle='#08051a'; ctx.fillRect(tx,GH-bh,44,bh);
    for(let bi=0;bi<4;bi++) ctx.fillRect(tx+bi*11,GH-bh-14,8,14);
    ctx.fillStyle='rgba(255,150,10,0.35)'; ctx.fillRect(tx+15,GH-bh+32,14,22);
  };
  [150,320,400,720,820,1200,1450,1600].forEach((bx,i)=>drawTower(bx,85+(i%3)*28));
}

// ─── DRAW: PLATFORM ──────────────────────────────────────────────────────────
function drawPlatform(ctx: CanvasRenderingContext2D, p: Plat, camX: number) {
  const dx=p.x-camX;
  if(dx+p.w<-4||dx>GW+4)return;
  ctx.fillStyle='#1c1410'; ctx.fillRect(dx,p.y,p.w,p.h);
  ctx.fillStyle='#3a2c1e'; ctx.fillRect(dx,p.y,p.w,5);
  ctx.strokeStyle='#281e14'; ctx.lineWidth=1;
  for(let bx=0;bx<=p.w;bx+=44){ctx.beginPath();ctx.moveTo(dx+bx,p.y);ctx.lineTo(dx+bx,p.y+p.h);ctx.stroke();}
  ctx.strokeStyle='#5a4430'; ctx.lineWidth=1.5; ctx.strokeRect(dx+0.75,p.y+0.75,p.w-1.5,p.h-1.5);
}

// ─── DRAW: SKELETON ──────────────────────────────────────────────────────────
function drawSkeleton(ctx: CanvasRenderingContext2D, cx: number, feetY: number, facing: 1|-1, anim: number, inv: number, isEnemy=false) {
  if(inv>0&&Math.floor(inv/6)%2===1){ctx.globalAlpha=0.25;}
  const bone=isEnemy?'#c83030':'#e0d8c0', eyeC=isEnemy?'#ff7070':'#080612', sc=isEnemy?0.84:1;
  const leg=Math.sin(anim*0.22)*11, arm=-leg;
  ctx.save(); ctx.translate(cx,feetY); ctx.scale(facing*sc,sc);
  ctx.strokeStyle=bone; ctx.lineWidth=3; ctx.lineCap='round';
  ctx.beginPath(); ctx.moveTo(-5,-10); ctx.lineTo(-9+leg*0.5,2); ctx.lineTo(-11+leg*0.35,14); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(5,-10); ctx.lineTo(9-leg*0.5,2); ctx.lineTo(11-leg*0.35,14); ctx.stroke();
  ctx.fillStyle=bone;
  ctx.beginPath(); ctx.arc(-11+leg*0.35,14,3.5,0,Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.arc(11-leg*0.35,14,3.5,0,Math.PI*2); ctx.fill();
  ctx.lineWidth=4; ctx.beginPath(); ctx.moveTo(-7,-10); ctx.lineTo(7,-10); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(0,-10); ctx.lineTo(0,-26); ctx.stroke();
  ctx.lineWidth=2;
  for(let r=0;r<3;r++){const ry=-13-r*5;ctx.beginPath();ctx.moveTo(0,ry);ctx.bezierCurveTo(9,ry-2,11,ry+4,7,ry+6);ctx.stroke();ctx.beginPath();ctx.moveTo(0,ry);ctx.bezierCurveTo(-9,ry-2,-11,ry+4,-7,ry+6);ctx.stroke();}
  ctx.lineWidth=3;
  ctx.beginPath(); ctx.moveTo(-4,-24); ctx.lineTo(-13+arm*0.4,-16); ctx.lineTo(-17+arm*0.6,-7); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(4,-24); ctx.lineTo(13-arm*0.4,-16); ctx.lineTo(17-arm*0.6,-7); ctx.stroke();
  ctx.fillStyle=bone;
  ctx.beginPath(); ctx.arc(-17+arm*0.6,-7,3.5,0,Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.arc(17-arm*0.6,-7,3.5,0,Math.PI*2); ctx.fill();
  ctx.fillStyle=bone; ctx.beginPath(); ctx.arc(0,-36,10.5,0,Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.arc(0,-30,8.5,0,Math.PI); ctx.fill();
  ctx.fillStyle=eyeC; ctx.beginPath(); ctx.arc(-4,-37,3,0,Math.PI*2); ctx.arc(4,-37,3,0,Math.PI*2); ctx.fill();
  ctx.fillStyle=bone; for(let t=-6;t<=6;t+=4){ctx.fillRect(t-1.8,-33,3.2,5);}
  ctx.restore(); ctx.globalAlpha=1;
}

// ─── DRAW: GHOST ─────────────────────────────────────────────────────────────
function drawGhost(ctx: CanvasRenderingContext2D, cx: number, feetY: number, anim: number) {
  const bob=Math.sin(anim*0.07)*5;
  ctx.save(); ctx.translate(cx,feetY-22+bob); ctx.globalAlpha=0.88;
  const g=ctx.createRadialGradient(0,-16,2,0,-16,22);
  g.addColorStop(0,'#9050cc'); g.addColorStop(1,'#380860');
  ctx.fillStyle=g; ctx.beginPath(); ctx.arc(0,-22,15,Math.PI,2*Math.PI); ctx.lineTo(15,-6);
  for(let i=0;i<3;i++) ctx.quadraticCurveTo(7.5-i*7.5,4+Math.sin(anim*0.09+i)*7,7.5-i*15,-6);
  ctx.closePath(); ctx.fill();
  ctx.fillStyle='#f040ff'; ctx.beginPath(); ctx.arc(-5.5,-24,3.5,0,Math.PI*2); ctx.arc(5.5,-24,3.5,0,Math.PI*2); ctx.fill();
  const gl=ctx.createRadialGradient(0,-16,4,0,-16,32);
  gl.addColorStop(0,'rgba(140,70,220,0.18)'); gl.addColorStop(1,'rgba(0,0,0,0)');
  ctx.fillStyle=gl; ctx.beginPath(); ctx.arc(0,-16,32,0,Math.PI*2); ctx.fill();
  ctx.restore(); ctx.globalAlpha=1;
}

// ─── DRAW: FIGURINE ──────────────────────────────────────────────────────────
function drawFig(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number) {
  const bob=Math.sin(frame*0.07+x*0.02)*4;
  ctx.save(); ctx.translate(x,y+bob);
  const gl=ctx.createRadialGradient(0,0,1,0,0,22);
  gl.addColorStop(0,'rgba(230,185,20,0.55)'); gl.addColorStop(1,'rgba(0,0,0,0)');
  ctx.fillStyle=gl; ctx.beginPath(); ctx.arc(0,0,22,0,Math.PI*2); ctx.fill();
  ctx.fillStyle='#f0c020'; ctx.beginPath(); ctx.arc(0,-5,6.5,0,Math.PI*2); ctx.fill();
  ctx.fillRect(-4.5,2,9,10); ctx.fillRect(-7.5,12,5.5,6); ctx.fillRect(2,12,5.5,6);
  ctx.fillStyle='rgba(255,255,200,0.65)'; ctx.beginPath(); ctx.arc(-2.5,-7,2.5,0,Math.PI*2); ctx.fill();
  ctx.restore();
}

// ─── DRAW: PORTAL ────────────────────────────────────────────────────────────
function drawPortal(ctx: CanvasRenderingContext2D, x: number, y: number, camX: number, frame: number) {
  const dx=x-camX; if(dx<-70||dx>GW+70)return;
  ctx.save(); ctx.translate(dx,y-30);
  for(let r=0;r<3;r++){const a=frame*0.025+r*(Math.PI*2/3);const pg=ctx.createRadialGradient(Math.cos(a)*9,Math.sin(a)*9,1,0,0,38);pg.addColorStop(0,`hsla(${270+r*40},80%,65%,0.75)`);pg.addColorStop(1,'rgba(0,0,0,0)');ctx.fillStyle=pg;ctx.beginPath();ctx.arc(0,0,38,0,Math.PI*2);ctx.fill();}
  const cg=ctx.createRadialGradient(0,0,2,0,0,20);
  cg.addColorStop(0,'#ffffff');cg.addColorStop(0.35,'#c060ff');cg.addColorStop(1,'#380070');
  ctx.fillStyle=cg;ctx.beginPath();ctx.arc(0,0,20,0,Math.PI*2);ctx.fill();
  ctx.font='bold 10px "JetBrains Mono",monospace';ctx.textAlign='center';ctx.fillStyle='#fff';
  ctx.shadowColor='#c060ff';ctx.shadowBlur=8;ctx.fillText('KONIEC',0,56);ctx.shadowBlur=0;
  ctx.restore();
}

// ─── DRAW: HUD ───────────────────────────────────────────────────────────────
function drawHUD(ctx: CanvasRenderingContext2D, lives: number, figs: number, score: number) {
  ctx.save();
  ctx.fillStyle='rgba(6,4,20,0.82)';ctx.fillRect(0,0,GW,40);
  ctx.strokeStyle='rgba(130,80,255,0.3)';ctx.lineWidth=1;ctx.strokeRect(0,0,GW,40);
  ctx.font='20px serif';ctx.textBaseline='middle';
  for(let i=0;i<3;i++){ctx.globalAlpha=i<lives?1:0.18;ctx.fillText('💀',12+i*29,20);}
  ctx.globalAlpha=1;
  ctx.font='13px "JetBrains Mono",monospace';ctx.fillStyle='#f0c020';
  ctx.shadowColor='#f0c020';ctx.shadowBlur=7;ctx.textAlign='center';
  ctx.fillText(`⚔ ${figs} / ${TOTAL_FIGS} FIGUREK`,GW/2,20);ctx.shadowBlur=0;
  ctx.fillStyle='#9060ff';ctx.textAlign='right';ctx.font='12px "JetBrains Mono",monospace';
  ctx.fillText(`WYNIK: ${score}`,GW-12,20);ctx.textAlign='left';ctx.textBaseline='alphabetic';
  ctx.restore();
}

// ─── DRAW: SAVING ANIMATION ──────────────────────────────────────────────────
function drawSaving(ctx: CanvasRenderingContext2D, sf: number, fig: number, preAcct: number) {
  ctx.save();
  // Dimming overlay
  ctx.fillStyle=`rgba(2,1,10,${Math.min(0.90,sf*0.035)})`;
  ctx.fillRect(0,0,GW,GH);

  if(fig===0){ctx.restore();return;}

  // ── Central badge ─────────────────────────────────────────────────────────
  const bx=GW/2, by=112;
  const badgeIn=Math.min(1,Math.max(0,(sf-4)*0.1));
  if(badgeIn>0){
    ctx.globalAlpha=badgeIn;
    // glow
    const og=ctx.createRadialGradient(bx,by,5,bx,by,95);
    og.addColorStop(0,'rgba(240,192,32,0.38)');og.addColorStop(1,'rgba(0,0,0,0)');
    ctx.fillStyle=og;ctx.beginPath();ctx.arc(bx,by,95,0,Math.PI*2);ctx.fill();
    // rotating outer ring
    ctx.save();ctx.translate(bx,by);ctx.rotate(sf*0.03);
    ctx.strokeStyle='#f0c020';ctx.lineWidth=2.5;
    ctx.setLineDash([8,6]);ctx.beginPath();ctx.arc(0,0,58,0,Math.PI*2);ctx.stroke();
    ctx.setLineDash([]);ctx.restore();
    // inner circle
    const bg=ctx.createRadialGradient(bx,by,6,bx,by,52);
    bg.addColorStop(0,'#2a1f04');bg.addColorStop(1,'#120e02');
    ctx.fillStyle=bg;ctx.beginPath();ctx.arc(bx,by,52,0,Math.PI*2);ctx.fill();
    ctx.strokeStyle='#f0c020';ctx.lineWidth=2;ctx.beginPath();ctx.arc(bx,by,52,0,Math.PI*2);ctx.stroke();
    // trophy emoji
    ctx.font='34px serif';ctx.textAlign='center';ctx.textBaseline='middle';
    ctx.fillText('🏆',bx,by);
    ctx.globalAlpha=1;
  }

  // ── Running counter ────────────────────────────────────────────────────────
  const FLIGHT=45;
  const landed=Math.min(fig,Math.max(0,Math.floor((sf-18)/7)));
  if(badgeIn>0){
    // +N big number
    ctx.globalAlpha=badgeIn;
    ctx.save();ctx.translate(bx,by+68);
    const pulse=1+Math.sin(sf*0.25)*0.06;
    ctx.scale(pulse,pulse);
    ctx.font='bold 24px "JetBrains Mono",monospace';
    ctx.textAlign='center';ctx.textBaseline='top';
    ctx.fillStyle='#f0c020';ctx.shadowColor='#f0c020';ctx.shadowBlur=12;
    ctx.fillText(`+${landed}`,0,0);ctx.shadowBlur=0;
    ctx.restore();
    // account total line
    ctx.font='12px "JetBrains Mono",monospace';
    ctx.fillStyle='rgba(240,200,60,0.7)';ctx.textAlign='center';ctx.textBaseline='top';
    ctx.fillText(`łącznie na koncie: ${preAcct+landed}`,bx,by+96);
    ctx.globalAlpha=1;
  }

  // ── Flying figurines ──────────────────────────────────────────────────────
  for(let i=0;i<fig;i++){
    const launchAt=18+i*7;
    const lp=LAUNCH_POS[i%15];
    const ft=sf-launchAt;

    if(ft<0){
      // bobbing at launch position
      const bob2=Math.sin((sf+i*17)*0.12)*5;
      ctx.save();ctx.translate(lp.x,lp.y+bob2);
      const gl2=ctx.createRadialGradient(0,0,1,0,0,16);
      gl2.addColorStop(0,'rgba(255,210,30,0.8)');gl2.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=gl2;ctx.beginPath();ctx.arc(0,0,16,0,Math.PI*2);ctx.fill();
      ctx.fillStyle='#f0c020';ctx.beginPath();ctx.arc(0,-3,5.5,0,Math.PI*2);ctx.fill();
      ctx.fillRect(-3.5,3,7,8);ctx.restore();
    } else if(ft<=FLIGHT){
      // flying toward badge
      const p=ft/FLIGHT;
      const ep=p<0.5?2*p*p:1-Math.pow(-2*p+2,2)/2; // ease-in-out
      const fx=lp.x+(bx-lp.x)*ep;
      const fy=lp.y+(by-lp.y)*ep-Math.sin(p*Math.PI)*100;
      const sc=1-ep*0.55;
      const alpha=1-ep*0.3;
      ctx.save();ctx.globalAlpha=alpha;ctx.translate(fx,fy);ctx.scale(sc,sc);
      const gl3=ctx.createRadialGradient(0,0,1,0,0,16);
      gl3.addColorStop(0,'rgba(255,220,40,0.9)');gl3.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=gl3;ctx.beginPath();ctx.arc(0,0,16,0,Math.PI*2);ctx.fill();
      ctx.fillStyle='#ffcc20';ctx.beginPath();ctx.arc(0,0,7,0,Math.PI*2);ctx.fill();
      ctx.restore();
      // arrival sparkle
      if(ft>FLIGHT-5){
        const sparkAlpha=Math.max(0,1-(ft-FLIGHT+5)/5)*0.9;
        for(let s=0;s<6;s++){
          const sa=(s/6)*Math.PI*2+sf*0.3;
          const sr=12+(ft-FLIGHT+5)*9;
          ctx.globalAlpha=sparkAlpha;ctx.fillStyle='#ffe060';
          ctx.beginPath();ctx.arc(bx+Math.cos(sa)*sr,by+Math.sin(sa)*sr,2.5,0,Math.PI*2);ctx.fill();
        }
        ctx.globalAlpha=1;
      }
    }
  }

  // ── Title text ────────────────────────────────────────────────────────────
  if(sf>22){
    const ta=Math.min(1,(sf-22)*0.08);
    ctx.globalAlpha=ta;
    ctx.font='bold 26px "Orbitron",sans-serif';
    ctx.textAlign='center';ctx.textBaseline='alphabetic';
    ctx.fillStyle='#c0a020';ctx.shadowColor='#c0a020';ctx.shadowBlur=14;
    ctx.fillText('FIGURKI LECĄ NA KONTO!',GW/2,232);ctx.shadowBlur=0;
    ctx.globalAlpha=1;
  }

  // ── All-collected flash ───────────────────────────────────────────────────
  const allLandedAt=18+(fig-1)*7+FLIGHT+12;
  if(sf>allLandedAt){
    const fa=Math.min(1,(sf-allLandedAt)*0.1);
    ctx.globalAlpha=fa;
    ctx.font='bold 18px "JetBrains Mono",monospace';
    ctx.textAlign='center';ctx.fillStyle='#60ff90';
    ctx.shadowColor='#60ff90';ctx.shadowBlur=12;
    ctx.fillText('✓ ZAPISANO NA KONCIE',GW/2,282);ctx.shadowBlur=0;
    ctx.globalAlpha=1;
  }

  ctx.restore();
}

// ─── DRAW: OVERLAY SCREENS ───────────────────────────────────────────────────
function drawOverlay(ctx: CanvasRenderingContext2D, type: 'start'|'dead'|'win', figs: number, score: number) {
  ctx.save();
  ctx.fillStyle='rgba(4,2,14,0.86)';ctx.fillRect(0,0,GW,GH);
  ctx.textAlign='center';

  if(type==='start'){
    ctx.font='bold 50px "Orbitron",sans-serif';
    ctx.fillStyle='#9060ff';ctx.shadowColor='#9060ff';ctx.shadowBlur=22;
    ctx.fillText('SOKAO-TOYS',GW/2,148);ctx.shadowBlur=0;
    ctx.font='bold 24px "Rajdhani",sans-serif';ctx.fillStyle='#c0a020';
    ctx.fillText('Przygody Małego Szkieletora',GW/2,198);
    ctx.font='15px "JetBrains Mono",monospace';ctx.fillStyle='#806090';
    ctx.fillText('Zbieraj złote figurki, depczaj szkielety, omijaj duchy!',GW/2,258);
    ctx.font='13px "JetBrains Mono",monospace';ctx.fillStyle='#c0b0e0';
    ctx.fillText('[ ← → ] lub [A D] — ruch     [ ↑ ] lub [W] lub [SPACJA] — skok',GW/2,306);
    ctx.fillText('Zbierz wszystkie 15 figurek lub dotrzyj do portalu na końcu!',GW/2,330);
    ctx.font='bold 17px "Orbitron",sans-serif';ctx.fillStyle='#ff80a8';
    ctx.shadowColor='#ff80a8';ctx.shadowBlur=9;
    if(Math.floor(Date.now()/500)%2) ctx.fillText('— NACIŚNIJ SPACJĘ LUB DOTKNIJ —',GW/2,420);
    ctx.shadowBlur=0;
  }

  if(type==='dead'){
    ctx.font='bold 54px "Orbitron",sans-serif';
    ctx.fillStyle='#ff3838';ctx.shadowColor='#ff3838';ctx.shadowBlur=22;
    ctx.fillText('GAME OVER',GW/2,170);ctx.shadowBlur=0;
    ctx.font='20px "Rajdhani",sans-serif';ctx.fillStyle='#c0a020';
    ctx.fillText(`Zebrano figurek: ${figs} / ${TOTAL_FIGS}`,GW/2,238);
    ctx.font='17px "JetBrains Mono",monospace';ctx.fillStyle='#9060ff';
    ctx.fillText(`Wynik: ${score}`,GW/2,278);
    ctx.font='bold 16px "Orbitron",sans-serif';ctx.fillStyle='#e0d0ff';
    ctx.shadowColor='#e0d0ff';ctx.shadowBlur=7;
    if(Math.floor(Date.now()/600)%2) ctx.fillText('SPACJA / DOTKNIJ — zagraj ponownie',GW/2,400);
    ctx.shadowBlur=0;
  }

  if(type==='win'){
    ctx.font='bold 46px "Orbitron",sans-serif';
    ctx.fillStyle='#c0a020';ctx.shadowColor='#c0a020';ctx.shadowBlur=22;
    ctx.fillText('BRAWO!',GW/2,152);ctx.shadowBlur=0;
    ctx.font='22px "Rajdhani",sans-serif';ctx.fillStyle='#9060ff';
    ctx.fillText(`Zebrano: ${figs} / ${TOTAL_FIGS} figurek`,GW/2,218);
    ctx.font='17px "JetBrains Mono",monospace';ctx.fillStyle='#e0d0ff';
    ctx.fillText(`Wynik końcowy: ${score}`,GW/2,260);
    if(figs===TOTAL_FIGS){
      ctx.font='14px "JetBrains Mono",monospace';ctx.fillStyle='#f0c020';
      ctx.fillText('★ BONUS +500 za wszystkie figurki!',GW/2,300);
    }
    ctx.font='bold 16px "Orbitron",sans-serif';ctx.fillStyle='#ff80a8';
    ctx.shadowColor='#ff80a8';ctx.shadowBlur=8;
    if(Math.floor(Date.now()/600)%2) ctx.fillText('SPACJA — zagraj ponownie',GW/2,400);
    ctx.shadowBlur=0;
  }

  ctx.textAlign='left';ctx.restore();
}

// ─── AABB ─────────────────────────────────────────────────────────────────────
function overlaps(ax:number,ay:number,aw:number,ah:number,bx:number,by:number,bw:number,bh:number){
  return ax<bx+bw&&ax+aw>bx&&ay<by+bh&&ay+ah>by;
}

// ─── COMPONENT ───────────────────────────────────────────────────────────────
export default function GamePage() {
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const afRef        = useRef<number>(0);
  const { user }     = useAuth();
  const navigate     = useNavigate();

  // Account figurines counter (loaded from Supabase, shown in top bar)
  const [accountFigs, setAccountFigs]   = useState(0);
  const [newFigsGained, setNewFigsGained] = useState(0);
  const accountFigsRef = useRef(0); // readable in game loop
  const gainTimerRef   = useRef<ReturnType<typeof setTimeout>>();

  // Communication refs (game loop ↔ React)
  const mb           = useRef({ left:false, right:false, jump:false });
  const quitRef      = useRef(false);
  const gameStateRef = useRef<GState>('start');

  // Supabase save (via ref to avoid re-running main effect)
  const saveRef = useRef<(figs:number,score:number,completed:boolean)=>Promise<void>>();

  const saveToSupabase = useCallback(async (figs:number,score:number,completed:boolean)=>{
    if(!user||figs===0)return;
    try{
      await (supabase as any).from('game_sessions').insert({
        user_id:user.id,figurines_collected:figs,score,completed,
      });
      const {data:prof} = await (supabase as any).from('profiles')
        .select('game_figurines,game_best_score').eq('id',user.id).single();
      const prev=prof??{game_figurines:0,game_best_score:0};
      await (supabase as any).from('profiles').update({
        game_figurines:(prev.game_figurines||0)+figs,
        game_best_score:Math.max(prev.game_best_score||0,score),
      }).eq('id',user.id);
      setAccountFigs(p=>p+figs);
      accountFigsRef.current+=figs;
      setNewFigsGained(figs);
      if(gainTimerRef.current) clearTimeout(gainTimerRef.current);
      gainTimerRef.current=setTimeout(()=>setNewFigsGained(0),4500);
      toast.success(`+${figs} figurek dodano do konta!`,{duration:3500});
    }catch(e){console.error('Błąd zapisu:',e);}
  },[user]);

  useEffect(()=>{saveRef.current=saveToSupabase;},[saveToSupabase]);

  // Load account figurines on mount / login
  useEffect(()=>{
    if(!user){setAccountFigs(0);accountFigsRef.current=0;return;}
    (async()=>{
      const {data}=await (supabase as any).from('profiles')
        .select('game_figurines').eq('id',user.id).single();
      const c=data?.game_figurines??0;
      setAccountFigs(c);accountFigsRef.current=c;
    })();
  },[user]);

  // ── Main game effect (runs ONCE) ────────────────────────────────────────────
  useEffect(()=>{
    const canvas=canvasRef.current;
    if(!canvas)return;
    const ctx=canvas.getContext('2d')!;

    let state:GState='start';
    const setState=(s:GState)=>{state=s;gameStateRef.current=s;};

    let px=60,py=394,pvx=0,pvy=0,pg=false;
    let pf:1|-1=1,pInv=0,pAnim=0,pLives=3;
    let camX=0,fig=0,score=0,frame=0;
    let saved=false,savingFrame=0,savingFinal:'dead'|'win'='dead',preAnimAcct=0;
    const keys=new Set<string>();

    const mkEnemies=():Enemy[]=>(ENEMIES_INIT.map(e=>({x:e.x,y:e.y,left:e.l,right:e.r,type:e.t,vx:1.3,alive:true,anim:0})));
    const mkFigs=()=>FIGS_INIT.map(f=>({...f,col:false}));
    let enemies:Enemy[]=mkEnemies();
    let figurines:Fig[]=mkFigs();

    const doSave=(figs:number,sc:number,done:boolean)=>{
      if(!saved){saved=true;saveRef.current?.(figs,sc,done);}
    };

    const triggerSaving=(final:'dead'|'win')=>{
      if(fig===0){setState(final);return;}
      preAnimAcct=accountFigsRef.current; // capture BEFORE async save updates it
      savingFrame=0;savingFinal=final;setState('saving');
    };

    const reset=()=>{
      setState('start');
      px=60;py=394;pvx=0;pvy=0;pg=false;
      pf=1;pInv=0;pAnim=0;pLives=3;camX=0;fig=0;score=0;frame=0;
      saved=false;savingFrame=0;
      enemies=mkEnemies();figurines=mkFigs();
    };

    const onKeyDown=(e:KeyboardEvent)=>{
      keys.add(e.code);
      if(e.code==='Space')e.preventDefault();
      if(['Space','ArrowUp','KeyW'].includes(e.code)){
        if(state==='start'){setState('playing');return;}
        if(state==='dead'||state==='win'){reset();return;}
      }
    };
    const onKeyUp=(e:KeyboardEvent)=>keys.delete(e.code);
    window.addEventListener('keydown',onKeyDown);
    window.addEventListener('keyup',onKeyUp);

    const loop=()=>{
      frame++;

      // ── Mobile tap: start / restart (checked every frame) ──────────────────
      const mbl=mb.current;
      if(mbl.jump){
        if(state==='start')  { setState('playing'); mbl.jump=false; }
        else if(state==='dead'||state==='win') { reset(); mbl.jump=false; }
      }

      // ── Quit signal ────────────────────────────────────────────────────────
      if(quitRef.current){
        quitRef.current=false;
        if(state==='playing'){
          doSave(fig,score,false);
          triggerSaving('dead');
        }
      }

      // ── Saving animation ───────────────────────────────────────────────────
      if(state==='saving'){
        savingFrame++;
        const dur=Math.max(130,18+(fig>0?fig:1)*7+70);
        if(savingFrame>=dur) setState(savingFinal);
      }

      // ── Playing logic ──────────────────────────────────────────────────────
      if(state==='playing'){
        const goLeft =keys.has('ArrowLeft')||keys.has('KeyA')||mbl.left;
        const goRight=keys.has('ArrowRight')||keys.has('KeyD')||mbl.right;
        const doJump =keys.has('ArrowUp')||keys.has('KeyW')||keys.has('Space')||mbl.jump;

        pvx=goLeft?-WALK_V:goRight?WALK_V:0;
        if(goLeft)pf=-1; if(goRight)pf=1;
        if(doJump&&pg){pvy=JUMP_V;pg=false;}

        pvy+=GRAV;px+=pvx;py+=pvy;pg=false;
        if(pvx!==0)pAnim++;
        if(pInv>0)pInv--;
        if(px<0)px=0;
        if(px+PW>LEVEL_W)px=LEVEL_W-PW;

        for(const p of PLATFORMS){
          if(!overlaps(px,py,PW,PH,p.x,p.y,p.w,p.h))continue;
          const prevBot=py+PH-pvy;
          if(prevBot<=p.y+5&&pvy>=0){py=p.y-PH;pvy=0;pg=true;}
          else{
            const ox=Math.min(px+PW,p.x+p.w)-Math.max(px,p.x);
            const oy=Math.min(py+PH,p.y+p.h)-Math.max(py,p.y);
            if(ox<oy){px+=px<p.x?-ox:ox;}
            else if(!pg){if(py<p.y){py-=oy;pvy=0;pg=true;}else{py+=oy;if(pvy<0)pvy=0;}}
          }
        }

        if(py>GH+60){
          pLives--;pInv=INV_DUR;px=Math.max(60,camX+40);py=360;pvx=0;pvy=0;
          if(pLives<=0){doSave(fig,score,false);triggerSaving('dead');}
        }

        const tc=px-GW*0.38;
        camX+=(tc-camX)*0.1;
        camX=Math.max(0,Math.min(LEVEL_W-GW,camX));

        for(const e of enemies){
          if(!e.alive)continue;
          e.x+=e.vx;e.anim+=Math.abs(e.vx);
          if(e.x<=e.left){e.x=e.left;e.vx=Math.abs(e.vx);}
          if(e.x>=e.right){e.x=e.right;e.vx=-Math.abs(e.vx);}
          if(pInv>0)continue;
          const ex=e.x-EW/2,ey=e.y-EH;
          if(!overlaps(px,py,PW,PH,ex,ey,EW,EH))continue;
          if(e.type==='skel'&&(py+PH-pvy)<=ey+14&&pvy>0){
            e.alive=false;pvy=-9.5;score+=50;
          }else{
            pLives--;pInv=INV_DUR;pvy=-6;pvx=pf*-3.5;
            if(pLives<=0){doSave(fig,score,false);triggerSaving('dead');}
          }
        }

        for(const f of figurines){
          if(f.col)continue;
          if(overlaps(px,py,PW,PH,f.x-13,f.y-14,26,26)){
            f.col=true;fig++;score+=100;
            // All figurines collected → instant win
            if(fig===TOTAL_FIGS){
              score+=500;doSave(fig,score,true);triggerSaving('win');
            }
          }
        }

        if(!saved&&overlaps(px,py,PW,PH,PORTAL_X-22,PORTAL_Y-44,44,60)){
          if(fig===TOTAL_FIGS)score+=500;
          doSave(fig,score,true);triggerSaving('win');
        }
      }

      // ── DRAW ──────────────────────────────────────────────────────────────
      drawBg(ctx,camX,frame);
      for(const p of PLATFORMS)drawPlatform(ctx,p,camX);
      drawPortal(ctx,PORTAL_X,PORTAL_Y,camX,frame);

      for(const f of figurines){
        if(f.col)continue;
        const dx=f.x-camX;
        if(dx<-30||dx>GW+30)continue;
        drawFig(ctx,dx,f.y,frame);
      }
      for(const e of enemies){
        if(!e.alive)continue;
        const dx=e.x-camX;
        if(dx<-50||dx>GW+50)continue;
        if(e.type==='ghost')drawGhost(ctx,dx,e.y,e.anim);
        else drawSkeleton(ctx,dx,e.y,e.vx>0?1:-1,e.anim,0,true);
      }

      if(state==='playing'||state==='saving')
        drawSkeleton(ctx,px-camX+PW/2,py+PH,pf,pAnim,pInv,false);
      if(state==='playing')drawHUD(ctx,pLives,fig,score);
      if(state==='saving') drawSaving(ctx,savingFrame,fig,preAnimAcct);
      if(state==='start')  drawOverlay(ctx,'start',0,0);
      if(state==='dead')   drawOverlay(ctx,'dead',fig,score);
      if(state==='win')    drawOverlay(ctx,'win',fig,score);

      afRef.current=requestAnimationFrame(loop);
    };

    afRef.current=requestAnimationFrame(loop);
    return()=>{
      cancelAnimationFrame(afRef.current);
      window.removeEventListener('keydown',onKeyDown);
      window.removeEventListener('keyup',onKeyUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const handleQuit=()=>{
    if(gameStateRef.current==='playing'){
      quitRef.current=true;
    } else {
      navigate('/');
    }
  };

  const handleCanvasTap=(e: React.PointerEvent)=>{
    e.preventDefault();
    mb.current.jump=true;
    setTimeout(()=>{mb.current.jump=false;},150);
  };

  return (
    <div className="fixed inset-0 bg-[#04020e] flex flex-col overflow-hidden">

      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-2 bg-[rgba(4,2,18,0.95)] border-b border-purple-900/40 shrink-0 z-10 gap-3">

        {/* Left: home */}
        <button onClick={()=>navigate('/')} className="flex items-center gap-2 text-sm text-purple-300 hover:text-white transition-colors font-mono shrink-0">
          <HomeIcon className="w-4 h-4" /><span className="hidden sm:inline">Strona główna</span>
        </button>

        {/* Center: title */}
        <span className="text-xs text-purple-400/40 font-mono tracking-widest hidden md:block">SOKAO-TOYS ADVENTURE</span>

        {/* Right: counter + quit */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Account figurines counter */}
          {user ? (
            <div className="flex items-center gap-1.5 px-3 py-1 bg-yellow-950/60 border border-yellow-600/40 rounded-lg">
              <span className="text-base leading-none">🏆</span>
              <span className="text-sm font-bold text-yellow-400 tabular-nums" style={{fontFamily:'JetBrains Mono,monospace'}}>
                {accountFigs}
              </span>
              {newFigsGained>0&&(
                <span className="text-xs font-bold text-green-400 animate-bounce" style={{fontFamily:'JetBrains Mono,monospace'}}>
                  +{newFigsGained}
                </span>
              )}
              <span className="text-xs text-yellow-600/60 hidden sm:inline" style={{fontFamily:'JetBrains Mono,monospace'}}>fig.</span>
            </div>
          ) : (
            <span className="text-xs text-purple-400/40 font-mono hidden sm:block">Zaloguj się → zbieraj figurki</span>
          )}

          {/* Quit button */}
          <button
            onClick={handleQuit}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-400 hover:text-white bg-red-950/40 hover:bg-red-700/60 border border-red-700/50 rounded-lg transition-all"
            style={{fontFamily:'JetBrains Mono,monospace'}}
          >
            <X className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Zakończ</span>
          </button>
        </div>
      </div>

      {/* ── Canvas ──────────────────────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center min-h-0 px-1 py-1">
        <canvas
          ref={canvasRef}
          width={GW} height={GH}
          onPointerDown={handleCanvasTap}
          style={{maxWidth:'100%',maxHeight:'100%',imageRendering:'pixelated',touchAction:'none',cursor:'crosshair'}}
        />
      </div>

      {/* ── Mobile controls — zawsze widoczne na małych ekranach ──────────────── */}
      <div className="shrink-0 flex items-center justify-between px-5 py-3 bg-[rgba(4,2,18,0.95)] border-t border-purple-900/40 select-none lg:hidden">
        <div className="flex gap-3">
          {(['left','right'] as const).map(dir=>(
            <button key={dir}
              className="w-16 h-14 bg-purple-900/50 border border-purple-600/50 rounded-xl text-white text-2xl font-bold active:bg-purple-700/70 touch-none"
              onPointerDown={e=>{e.currentTarget.setPointerCapture(e.pointerId);mb.current[dir]=true;}}
              onPointerUp={()=>{mb.current[dir]=false;}}
              onPointerCancel={()=>{mb.current[dir]=false;}}
              onPointerLeave={()=>{mb.current[dir]=false;}}
            >{dir==='left'?'←':'→'}</button>
          ))}
        </div>
        <button
          className="w-20 h-14 bg-yellow-900/50 border border-yellow-600/50 rounded-xl text-yellow-200 text-sm font-bold active:bg-yellow-700/70 touch-none"
          onPointerDown={e=>{e.currentTarget.setPointerCapture(e.pointerId);mb.current.jump=true;}}
          onPointerUp={()=>{mb.current.jump=false;}}
          onPointerCancel={()=>{mb.current.jump=false;}}
          onPointerLeave={()=>{mb.current.jump=false;}}
        >↑ SKOK</button>
      </div>
    </div>
  );
}
